import {
  type CollectionRequirementSettings,
  FOLLOWUP_MODE_SOURCE,
  findConditionRuleProblem,
  GOAL_HAS_MANDATE_SOURCE,
  GOAL_HAS_RISK_SOURCE,
  GOAL_RISK_SCORE_CAPPED_SOURCE,
  GOAL_RISK_SCORE_SOURCE,
  GOAL_TYPE_SOURCE,
  getLocalizedValue,
  getSettingForComponentType,
  isComponentTypeAllowed,
  isConditionAllowedInScope,
  isConditionSourceCustomFieldType,
  isOptionFieldType,
  isProductAssessmentSource,
  isQuizResultSource,
  isScoreConditionSource,
  isValueRequiredOperator,
  menuConditionOperator,
  operatorForNewSource,
  PORTFOLIO_HAS_MANDATE_CHECK_SOURCE,
  PORTFOLIO_MANDATE_STATUS_SOURCE,
  PORTFOLIO_MODEL_DEVIATION_SOURCE,
  PORTFOLIO_SCHEDULE_SOURCE,
  PORTFOLIO_TYPE_SOURCE,
  pdfSectionScopeToConditionScope,
  QUIZ_RESULT_SOURCE_PREFIX,
  RISK_SCORE_CAP_STATES,
  type RiskScoreCapState,
  renderedRiskSectionsInPdfSection,
  resolveRiskInputScopes,
  SENTINEL_SOURCE_SCOPES,
  scalarConditionOperators,
} from "@repo/shared";
import type {
  ConfiguredAssessmentType,
  CustomFieldOption,
  FlowQuizSummary,
  PdfAdvisorNotePage,
  PdfSectionScope,
  RiskSection,
  Translated,
} from "@repo/shared/types";
import {
  ADVISOR_NOTE_FIELD_PREFIX,
  advisorNotePageDisplayNames,
  isComponentAllowedInSection,
  isPdfCurrencyBlockType,
  RISK_SECTIONS,
} from "@repo/shared/types";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  assessmentSourceValueOptions,
  buildAssessmentPickerEntries,
  buildQuizScorePickerEntries,
  quizResultValueOptions,
  scoreSourcePassScore,
} from "@/components/conditional-display/assessment-picker-entries";
import { conditionRuleProblemMessage } from "@/components/conditional-display/condition-rule-problem-message";
import { GoalTypeConditionPicker } from "@/components/conditional-display/goal-type-condition-picker";
import { ScalarOperatorItems } from "@/components/conditional-display/scalar-operator-items";
import { ScoreConditionValueInput } from "@/components/conditional-display/score-condition-value-input";
import { useCountryOptions } from "@/components/country-select";
import { getPdfComponentLabel } from "@/components/pdf-templates/pdf-component-labels";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import {
  Dialog,
  DialogBody,
  DialogCancel,
  DialogDescription,
  DialogScrollContent,
  DialogScrollFooter,
  DialogScrollHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchInput } from "@/components/ui/search-input";
import { SearchableSelect, type SearchableSelectOption } from "@/components/ui/searchable-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAddComponent, useUpdateComponent } from "@/lib/hooks/use-pdf-templates";
import { useGoalTypes } from "@/lib/hooks/use-portfolio-collections";
import { useRiskModel } from "@/lib/hooks/use-risk-models";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import {
  buildFieldMap,
  hasAdvisorNotePrefix,
  isFieldId,
  QUIZ_EXTERNAL_SUFFIX,
  QUIZ_OVERRIDE_SUFFIX,
  splitQuizRef,
  transformToDisplay,
  transformToStorage,
} from "@/lib/services/placeholder-transformer";
import { getComponentIcon } from "../../shared/component-icons";
import { T } from "./component-dialog.i18n";
import type { FlowField } from "./component-forms";
import {
  AdvisorImageForm,
  AssetAllocationForm,
  AssetClassForecastForm,
  COMPONENT_TYPE_METADATA,
  ComplianceChartForm,
  type ComponentTypeInfo,
  CostChartForm,
  CostTableForm,
  ExpectedValueForm,
  FinancialSituationForm,
  GoalListForm,
  getDefaultConfigForType,
  HeadingForm,
  HistoricalReturnForm,
  ImageForm,
  NO_ADVISOR_NOTE_PAGES,
  PortfolioDisplayForm,
  PortfolioForecastForm,
  PortfolioXrayForm,
  ProductCostsForm,
  RiskAssessmentForm,
  SpacerForm,
  TableForm,
  TextForm,
  TitleOnlyForm,
  validateAdvisorImageConfig,
  validateAssetAllocationConfig,
  validateAssetClassForecastConfig,
  validateComplianceChartConfig,
  validateExpectedValueConfig,
  validateHeadingConfig,
  validateImageConfig,
  validatePortfolioForecastConfig,
  validatePortfolioXrayConfig,
  validateRiskAssessmentConfig,
  validateSpacerConfig,
  validateTableConfig,
  validateTextConfig,
} from "./component-forms";
import type { ColumnPosition, ConditionalOperator, PdfComponent, PdfComponentType } from "./types";

/** QL-212 — the risk block's four sections, as translation keys (mirrors the flow picker). */
const RISK_SECTION_KEYS = {
  timeHorizon: "riskSectionTimeHorizon",
  riskLevel: "riskSectionRiskLevel",
  adjustmentQuestions: "riskSectionAdjustmentQuestions",
  result: "riskSectionResult",
} as const satisfies Record<RiskSection, keyof typeof T.en_GB>;

// Helper to get component type info with translations
function getComponentTypes(t: Translated<typeof T>, lang: string): ComponentTypeInfo[] {
  const descriptionMap: Record<PdfComponentType, string> = {
    text: t.textDescription,
    heading: t.headingDescription,
    table_of_contents: t.tableOfContentsDescription,
    financial_situation: t.financialSituationDescription,
    image: t.imageDescription,
    advisor_image: t.advisorImageDescription,
    page_break: t.pageBreakDescription,
    spacer: t.spacerDescription,
    table: t.tableDescription,
    portfolio_display: t.portfolioDisplayDescription,
    portfolio_xray: t.portfolioXrayDescription,
    asset_allocation: t.assetAllocationDescription,
    asset_class_forecast: t.assetClassForecastDescription,
    portfolio_forecast: t.portfolioForecastDescription,
    historical_return: t.historicalReturnDescription,
    expected_value: t.expectedValueDescription,
    risk_assessment: t.riskAssessmentDescription,
    cost_table: t.costTableDescription,
    cost_chart: t.costChartDescription,
    portfolio_costs: t.portfolioCostsDescription,
    product_costs: t.productCostsDescription,
    client_classification: t.clientClassificationDescription,
    product_assessment: t.productAssessmentDescription,
    quiz_results: t.quizResultsDescription,
    trapets_lookup: t.trapetsLookupDescription,
    sumsub_verification: t.sumsubVerificationDescription,
    enin_lookup: t.eninLookupDescription,
    group_members: t.groupMembersDescription,
    client_parties: t.clientPartiesDescription,
    goal_info: t.goalInfoDescription,
    goal_list: t.goalListDescription,
    wealth_plan: t.wealthPlanDescription,
    sustainability_preferences: t.sustainabilityPreferencesDescription,
    sustainability_status: t.sustainabilityStatusDescription,
    client_preferences: t.clientPreferencesDescription,
    volatility_chart: t.volatilityChartDescription,
    equity_share_chart: t.equityShareChartDescription,
    mandate: t.mandateDescription,
    mandate_comparison: t.mandateComparisonDescription,
    mandate_portfolio: t.mandatePortfolioDescription,
  };

  const detailedDescriptionMap: Record<PdfComponentType, string> = {
    text: t.textDetailedDescription,
    heading: t.headingDetailedDescription,
    table_of_contents: t.tableOfContentsDetailedDescription,
    financial_situation: t.financialSituationDetailedDescription,
    image: t.imageDetailedDescription,
    advisor_image: t.advisorImageDetailedDescription,
    page_break: t.pageBreakDetailedDescription,
    spacer: t.spacerDetailedDescription,
    table: t.tableDetailedDescription,
    portfolio_display: t.portfolioDisplayDetailedDescription,
    portfolio_xray: t.portfolioXrayDetailedDescription,
    asset_allocation: t.assetAllocationDetailedDescription,
    asset_class_forecast: t.assetClassForecastDetailedDescription,
    portfolio_forecast: t.portfolioForecastDetailedDescription,
    historical_return: t.historicalReturnDetailedDescription,
    expected_value: t.expectedValueDetailedDescription,
    risk_assessment: t.riskAssessmentDetailedDescription,
    cost_table: t.costTableDetailedDescription,
    cost_chart: t.costChartDetailedDescription,
    portfolio_costs: t.portfolioCostsDetailedDescription,
    product_costs: t.productCostsDetailedDescription,
    client_classification: t.clientClassificationDetailedDescription,
    product_assessment: t.productAssessmentDetailedDescription,
    quiz_results: t.quizResultsDetailedDescription,
    trapets_lookup: t.trapetsLookupDetailedDescription,
    sumsub_verification: t.sumsubVerificationDetailedDescription,
    enin_lookup: t.eninLookupDetailedDescription,
    group_members: t.groupMembersDetailedDescription,
    client_parties: t.clientPartiesDetailedDescription,
    goal_info: t.goalInfoDetailedDescription,
    goal_list: t.goalListDetailedDescription,
    wealth_plan: t.wealthPlanDetailedDescription,
    sustainability_preferences: t.sustainabilityPreferencesDetailedDescription,
    sustainability_status: t.sustainabilityStatusDetailedDescription,
    client_preferences: t.clientPreferencesDetailedDescription,
    volatility_chart: t.volatilityChartDetailedDescription,
    equity_share_chart: t.equityShareChartDetailedDescription,
    mandate: t.mandateDetailedDescription,
    mandate_comparison: t.mandateComparisonDetailedDescription,
    mandate_portfolio: t.mandatePortfolioDetailedDescription,
  };

  return COMPONENT_TYPE_METADATA.map((meta) => ({
    ...meta,
    label: getPdfComponentLabel(meta.type, lang),
    description: descriptionMap[meta.type] || "",
    detailedDescription: detailedDescriptionMap[meta.type] || "",
  }));
}

// Helper to get category labels with translations
function getComponentCategories(t: Translated<typeof T>): Array<{ id: string; label: string }> {
  return [
    { id: "general", label: t.categoryGeneral },
    { id: "data", label: t.categoryData },
    { id: "portfolio", label: t.categoryPortfolio },
    { id: "costs", label: t.categoryCosts },
    { id: "client", label: t.categoryClient },
    { id: "goal", label: t.categoryGoal },
  ];
}

// Helper to get component descriptions for TitleOnlyForm
function getComponentDescriptions(t: Translated<typeof T>): Record<string, string> {
  return {
    table_of_contents: t.tableOfContentsListed,
    client_classification: t.clientClassificationDetailedDescription,
    product_assessment: t.productAssessmentDetailedDescription,
    quiz_results: t.quizResultsDetailedDescription,
    trapets_lookup: t.trapetsLookupDetailedDescription,
    sumsub_verification: t.sumsubVerificationDetailedDescription,
    enin_lookup: t.eninLookupDetailedDescription,
    group_members: t.groupMembersDetailedDescription,
    client_parties: t.clientPartiesDetailedDescription,
    cost_table: t.costTableDetailedDescription,
    portfolio_costs: t.portfolioCostsDetailedDescription,
    product_costs: t.productCostsDetailedDescription,
    wealth_plan: t.wealthPlanDetailedDescription,
    sustainability_preferences: t.sustainabilityPreferencesDetailedDescription,
    sustainability_status: t.sustainabilityStatusDetailedDescription,
    client_preferences: t.clientPreferencesDetailedDescription,
    goal_info: t.goalInfoDetailedDescription,
    goal_list: t.goalListDetailedDescription,
    volatility_chart: t.volatilityChartDetailedDescription,
    equity_share_chart: t.equityShareChartDetailedDescription,
    mandate: t.mandateDetailedDescription,
    mandate_comparison: t.mandateComparisonDetailedDescription,
    mandate_portfolio: t.mandatePortfolioDetailedDescription,
  };
}

type ClientCustomField = {
  id: string;
  name: string;
  type: string;
  // Option rows with stable ids (QL-123); condition values store the id.
  options?: CustomFieldOption[] | null;
  isActive: boolean;
  isStandard?: boolean;
};

type ComponentDialogProps = {
  open: boolean;
  onClose: (success: boolean) => void;
  flowFields: FlowField[];
  /** QL-13 — the `quiz` components on this template's flow. */
  quizzes?: FlowQuizSummary[];
  /** QL-414 — the flow pages that take advisor notes: the `AdvisorNote.*` fields. */
  advisorNotePages?: PdfAdvisorNotePage[];
  /** QL-414 — while true that list is a placeholder (loading or failed), which must not read as "no such page". */
  advisorNotePagesUnknown?: boolean;
  /** QL-13 — while true the quiz list is a placeholder (loading or failed), which must not read as "all dangling". */
  quizzesUnknown?: boolean;
  /** QL-361 — the configured assessment types of this template's flow. */
  configuredAssessmentTypes?: ConfiguredAssessmentType[];
  /** QL-361 — while true the list is a placeholder (loading or failed), which must not read as "all stale". */
  assessmentTypesUnknown?: boolean;
  clientCustomFields?: ClientCustomField[];
  hasXRayAccess?: boolean;
  hasRiskModel?: boolean;
  portfolioCollectionId?: string | null;
  templateLanguage: string;
  collectionSettings?: CollectionRequirementSettings | null;
  sectionScope?: PdfSectionScope;
  /** QL-414 — the page has no Heading component yet, so a new one is its first. */
  isFirstHeading?: boolean;
  /**
   * QL-212 — the flow's risk model, so the picker can say whether a
   * risk-assessment placement would print anything in THIS section scope. Null
   * while it is unknown (still loading, or the flow has no model), which never
   * blocks.
   */
  riskModelId?: string | null;
  // For add mode
  pageId?: string;
  layoutId?: string | null;
  columnPosition?: ColumnPosition;
  // For edit mode
  component?: PdfComponent | null;
};

/** Apply `fn` to a config's string `title` and `description`, leaving the rest untouched. */
function mapTitleAndDescription(
  config: Record<string, unknown>,
  fn: (value: string) => string,
): Record<string, unknown> {
  const next = { ...config };
  for (const key of ["title", "description"] as const) {
    const value = next[key];
    if (typeof value === "string" && value) next[key] = fn(value);
  }
  return next;
}

export function ComponentDialog({
  open,
  onClose,
  flowFields,
  quizzes: activeQuizzes = [],
  advisorNotePages = NO_ADVISOR_NOTE_PAGES,
  advisorNotePagesUnknown = false,
  quizzesUnknown = false,
  configuredAssessmentTypes = [],
  assessmentTypesUnknown = false,
  clientCustomFields = [],
  hasXRayAccess = false,
  hasRiskModel = false,
  portfolioCollectionId = null,
  templateLanguage,
  collectionSettings,
  sectionScope,
  isFirstHeading,
  riskModelId = null,
  pageId,
  layoutId = null,
  columnPosition = null,
  component,
}: ComponentDialogProps) {
  const { userLanguage, tenantDefaultLanguage } = useUserLanguage();
  const t = useT(T);
  const countryOptions = useCountryOptions();
  const addComponentMutation = useAddComponent();
  const updateComponentMutation = useUpdateComponent();

  // Get translated component types, categories, and descriptions
  const componentTypes = useMemo(() => getComponentTypes(t, userLanguage), [t, userLanguage]);
  const componentCategories = useMemo(() => getComponentCategories(t), [t]);
  const componentDescriptions = useMemo(() => getComponentDescriptions(t), [t]);

  // Helper to get component label from translated types
  const getComponentLabel = (type: PdfComponentType): string => {
    const found = componentTypes.find((ct) => ct.type === type);
    return found?.label || type;
  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Mode detection
  const isEditMode = !!component;

  // Step for add mode: "select" (choose component type) or "configure" (set up component)
  const [step, setStep] = useState<"select" | "configure">("select");

  // Expanded categories in the picker
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(["general"]));

  // Search query for component picker
  const [searchQuery, setSearchQuery] = useState("");

  // Component type (for add mode, user selects; for edit mode, comes from component)
  const [componentType, setComponentType] = useState<PdfComponentType | "">("");

  // Generic config state - stores the form data
  const [config, setConfig] = useState<Record<string, unknown>>({});

  // Conditional display state
  const [conditionalEnabled, setConditionalEnabled] = useState(false);
  const [sourceFieldId, setSourceFieldId] = useState("");
  const [storedOperator, setOperator] = useState<ConditionalOperator>("equals");
  const [conditionValue, setConditionValue] = useState("");

  // Clear conditionValue when sourceFieldId changes (different fields have different valid values)
  // and keep the operator valid for the new source.
  const handleSourceFieldChange = (newFieldId: string) => {
    setSourceFieldId(newFieldId);
    setConditionValue("");
    const newCustomField = newFieldId.startsWith("__custom_field_")
      ? clientCustomFields.find((f) => f.id === newFieldId.replace("__custom_field_", ""))
      : undefined;
    const fieldType =
      newCustomField?.type ?? flowFields.find((f) => f.id === newFieldId)?.type ?? "";
    setOperator(operatorForNewSource(operator, { sourceId: newFieldId, fieldType }));
  };

  // QL-120 — the goal-type source is only offered where a current goal exists
  // at render time (per_goal / per_portfolio sections), and only when the
  // flow's collection defines goal types. Offered while loading so an existing
  // rule's source does not flash as unrecognised.
  const { goalTypes: collectionGoalTypes, isLoading: goalTypesLoading } = useGoalTypes(
    portfolioCollectionId ?? null,
  );
  // QL-161: a condition may only read sources evaluable at the enclosing
  // section's scope (global < goal < portfolio) — same registry + predicate
  // as the server-side guard, so the picker and the reject path agree.
  const containerScope = pdfSectionScopeToConditionScope(sectionScope ?? "global");
  const offerSentinel = (sourceId: string) =>
    isConditionAllowedInScope(SENTINEL_SOURCE_SCOPES[sourceId] ?? "global", containerScope);
  const conditionFlowFields = flowFields.filter((f) =>
    isConditionAllowedInScope(f.scope ?? "global", containerScope),
  );
  // QL-13 — quiz results are global sources, offerable in every section.
  const canOfferGoalType =
    offerSentinel(GOAL_TYPE_SOURCE) &&
    !!portfolioCollectionId &&
    (goalTypesLoading || collectionGoalTypes.length > 0);

  // Filter component types based on section scope, access, and flow configuration
  const availableComponentTypes = useMemo(
    () =>
      componentTypes.filter((ct) => {
        // Filter out page_break when adding to a layout
        if (layoutId && ct.type === "page_break") return false;
        // Filter out X-Ray if no access
        if (ct.requiresXRay && !hasXRayAccess) return false;
        // Filter out components that require risk model if flow has no risk model
        if (ct.requiresRiskModel && !hasRiskModel) return false;
        // Filter out components that require portfolio collection if flow has no portfolio collection
        if (ct.requiresPortfolioCollection && !portfolioCollectionId) return false;
        // Filter based on section scope
        if (sectionScope && !isComponentAllowedInSection(ct.type, sectionScope)) return false;
        return true;
      }),
    [componentTypes, layoutId, hasXRayAccess, hasRiskModel, portfolioCollectionId, sectionScope],
  );

  // Whether a component type is blocked by the assigned collection's requirement
  // settings. Fails open while settings are loading (settings === null); the server
  // is the authoritative guard. Ungoverned types are always allowed.
  const isBlockedByCollectionSettings = (type: PdfComponentType): boolean =>
    !!collectionSettings && !isComponentTypeAllowed(collectionSettings, type);

  // QL-212 (D11) — which parts of the risk assessment print in THIS section
  // scope, for this flow's risk model (the flow rule: each input prints where
  // its scope says). `null` while the model is still loading (or when the flow
  // has none): an unloaded model normalises to all-per-goal, which in a global
  // section would read as "prints nothing" and wrongly block the Add button.
  // The service guard is the authority; this only stops the designer walking
  // into its refusal.
  const { model: riskModel } = useRiskModel(riskModelId);
  const riskSections: ReadonlySet<RiskSection> | null = useMemo(
    () =>
      riskModel && sectionScope
        ? renderedRiskSectionsInPdfSection(resolveRiskInputScopes(riskModel), sectionScope)
        : null,
    [riskModel, sectionScope],
  );
  const isBlockedByEmptyRiskSections = (type: PdfComponentType): boolean =>
    type === "risk_assessment" && riskSections?.size === 0;

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setError(null);
      setLoading(false);

      if (isEditMode && component) {
        // Edit mode: load existing config
        setComponentType(component.componentType as PdfComponentType);
        setStep("configure"); // Skip selection step in edit mode
        try {
          const existingConfig = component.config ? JSON.parse(component.config) : {};
          setConfig(existingConfig);

          // Load conditional display settings
          const conditional = existingConfig?.conditionalDisplay;
          if (conditional) {
            setConditionalEnabled(conditional.enabled || false);
            setSourceFieldId(conditional.sourceFieldId || "");
            setOperator(conditional.operator || "equals");
            setConditionValue(conditional.value || "");
          } else {
            resetConditional();
          }
        } catch {
          setConfig(getDefaultConfigForType(component.componentType as PdfComponentType));
          resetConditional();
        }
      } else {
        // Add mode: reset everything and start at selection step
        setStep("select");
        setSearchQuery("");
        // Default select first available component and expand its category
        const firstType = availableComponentTypes[0]?.type || "";
        const firstCategory = availableComponentTypes[0]?.category || "general";
        setComponentType(firstType);
        setConfig(
          firstType ? getDefaultConfigForType(firstType, { sectionScope, isFirstHeading }) : {},
        );
        setExpandedCategories(new Set([firstCategory]));
        resetConditional();
      }
    }
  }, [open, component, isEditMode, availableComponentTypes]);

  const resetConditional = () => {
    setConditionalEnabled(false);
    setSourceFieldId("");
    setOperator("equals");
    setConditionValue("");
  };

  // When component type changes in add mode, set default config
  const handleComponentTypeChange = (type: PdfComponentType) => {
    setComponentType(type);
    setConfig(getDefaultConfigForType(type, { sectionScope, isFirstHeading }));
  };

  // Validate the current config
  const validateConfig = (): string | null => {
    if (!componentType) {
      return "Please select a component type";
    }

    const ruleProblem = conditionalEnabled
      ? findConditionRuleProblem(
          { sourceFieldId, operator, value: conditionValue },
          {
            quizzes: quizzesUnknown ? null : activeQuizzes,
            assessmentTypes: assessmentTypesUnknown ? null : configuredAssessmentTypes,
          },
        )
      : null;
    // A dangling quiz names the quizzes still on the flow, so the author knows what to pick.
    if (ruleProblem === "missing_quiz") {
      return activeQuizzes.length > 0
        ? t.missingQuizPickOneOf(activeQuizzes.map((q) => q.name).join(", "))
        : t.missingQuizNoneLeft;
    }
    if (ruleProblem) return conditionRuleProblemMessage(ruleProblem, userLanguage);

    // Validate component-specific config
    switch (componentType) {
      case "text":
        return validateTextConfig(config as any, userLanguage);
      case "heading":
        return validateHeadingConfig(config as any, userLanguage);
      case "table":
        return validateTableConfig(config as any, userLanguage);
      case "image":
        return validateImageConfig(config as any, userLanguage);
      case "advisor_image":
        return validateAdvisorImageConfig(config as any);
      case "spacer":
        return validateSpacerConfig(config as any, userLanguage);
      case "portfolio_xray":
        return validatePortfolioXrayConfig(config as any, userLanguage);
      case "asset_allocation":
        return validateAssetAllocationConfig(config as any, userLanguage);
      case "asset_class_forecast":
        return validateAssetClassForecastConfig(config as any, userLanguage);
      case "volatility_chart":
      case "equity_share_chart":
        return validateComplianceChartConfig(config as any, userLanguage);
      case "expected_value":
        return validateExpectedValueConfig(config as any, userLanguage);
      case "risk_assessment":
        return validateRiskAssessmentConfig(config as any);
      case "portfolio_forecast":
        return validatePortfolioForecastConfig(config as any, userLanguage);
      default:
        return null;
    }
  };

  // Validate field placeholders in text content
  const validateFieldPlaceholders = (
    text: string,
    // {{page}} is Typst code, so it only renders in text and heading content;
    // a component title or description would print "-".
    allowPage = true,
  ): { valid: boolean; error?: string } => {
    const placeholderRegex = /\{\{([^}]+)\}\}/g;
    const matches = Array.from(text.matchAll(placeholderRegex));

    if (matches.length === 0) {
      return { valid: true };
    }

    const validClientStandardFields = new Set([
      "client.name",
      "client.type",
      "client.email",
      "client.mobilephone",
      "client.ssn",
      "client.orgid",
      "client.country",
      "client.owner",
      "client.members",
      "client.representative",
    ]);
    const validClientCustomFieldIds = new Set(clientCustomFields.map((f) => f.id.toLowerCase()));
    const validClientCustomFieldNames = new Set(
      clientCustomFields.map((f) => f.name.toLowerCase()),
    );
    const validFlowFieldIds = new Set(flowFields.map((f) => f.id.toLowerCase()));
    const validFlowFieldNames = new Set(flowFields.map((f) => f.name.toLowerCase()));
    // QL-610 — a name two fields share cannot say which one it means. The
    // transformer owns that rule; this only runs at submit, after the map
    // below is built.
    const { ambiguousFlowFieldNames } = placeholderFieldMap;
    const availableFieldNames = flowFields.map((f) => f.name);
    const validQuizIds = new Set(activeQuizzes.map((q) => q.id.toLowerCase()));
    const validQuizNames = new Set(activeQuizzes.map((q) => q.name.toLowerCase()));

    // Special fields that are always valid
    const specialFields = [
      "risk score",
      "goal name",
      "portfolio name",
      "goal type",
      "goal description",
      "goal platform name",
      "goal advisor notes",
      "advisor notes",
      "classification type",
      // QL-375 group 10 — the goal's mandate placeholders (Goal Fields in the Insert menu)
      "mandate name",
      "mandate status",
      "mandate source",
      // QL-664 — used / updated / created.
      "mandate origin",
      "mandate mode",
      "existing mandate name",
      "mandate deviation note",
      "mandate decided by",
      "mandate decided at",
      // QL-375 group 10b — the portfolio's own verdict and decision (Portfolio Fields)
      "portfolio mandate status",
      "portfolio mandate deviation note",
      "portfolio mandate decided by",
      "portfolio mandate decided at",
      // QL-72 — model portfolio picked from another risk score (Portfolio Fields)
      "portfolio model deviation",
      "portfolio model risk score",
      "portfolio goal risk score",
      "today",
      ...(allowPage ? ["page"] : []),
    ];

    // Standard Owner.* fields (advisor / client owner data)
    const validOwnerFields = new Set([
      "owner.name",
      "owner.email",
      "owner.phone",
      "owner.title",
      "owner.signature",
    ]);
    const ownerFieldList = "Owner.Name, Owner.Email, Owner.Phone, Owner.Title, Owner.Signature";

    // Standard Flow.* fields (flow instance data)
    const validFlowStandardFields = new Set([
      "flow.name",
      "flow.cancelreason",
      // QP-176 — expecting-financial-changes answer + note.
      "flow.expectingfinancialchanges",
      "flow.expectingfinancialchangesnote",
    ]);

    const standardClientFieldList =
      "Client.Name, Client.Type, Client.Email, Client.MobilePhone, Client.SSN, Client.OrgId, Client.Country, Client.Owner";
    const customClientFieldList = clientCustomFields.map((f) => `Client.${f.name}`).join(", ");
    const customFlowFieldList = flowFields.map((f) => `Flow.${f.name}`).join(", ");
    const quizFieldList = activeQuizzes
      .flatMap((q) => [
        `Quiz.${q.name}`,
        `Quiz.${q.name}${QUIZ_OVERRIDE_SUFFIX}`,
        `Quiz.${q.name}${QUIZ_EXTERNAL_SUFFIX}`,
      ])
      .join(", ");

    for (const match of matches) {
      const fieldRef = match[1].trim();

      // Skip special fields
      if (specialFields.includes(fieldRef.toLowerCase())) continue;

      // Check Owner.* fields (advisor / client owner data)
      if (fieldRef.toLowerCase().startsWith("owner.")) {
        if (validOwnerFields.has(fieldRef.toLowerCase())) {
          continue;
        }
        return {
          valid: false,
          error: `Invalid owner field: ${fieldRef}. Valid options: ${ownerFieldList}`,
        };
      }

      // Check Client.* fields
      if (fieldRef.toLowerCase().startsWith("client.")) {
        const fullLower = fieldRef.toLowerCase();
        const subRef = fieldRef.substring("client.".length).toLowerCase();
        if (
          validClientStandardFields.has(fullLower) ||
          validClientCustomFieldIds.has(subRef) ||
          validClientCustomFieldNames.has(subRef)
        ) {
          continue;
        }
        const validOptions = customClientFieldList
          ? `${standardClientFieldList}, ${customClientFieldList}`
          : standardClientFieldList;
        return {
          valid: false,
          error: `Invalid client field: ${fieldRef}. Valid options: ${validOptions}`,
        };
      }

      // Check Flow.* fields (flow instance data)
      if (fieldRef.toLowerCase().startsWith("flow.")) {
        const fullLower = fieldRef.toLowerCase();
        const subRef = fieldRef.substring("flow.".length).toLowerCase();
        // A standard placeholder like {{Flow.Name}} wins over custom fields
        // that happen to share its name
        if (validFlowStandardFields.has(fullLower) || validFlowFieldIds.has(subRef)) {
          continue;
        }
        if (ambiguousFlowFieldNames.has(subRef)) {
          return {
            valid: false,
            error: `Flow field name "${fieldRef.substring("flow.".length)}" is used by more than one field. Insert the field from the field list, or rename one of them.`,
          };
        }
        if (validFlowFieldNames.has(subRef)) {
          continue;
        }
        const flowStandardList =
          "Flow.Name, Flow.CancelReason, Flow.ExpectingFinancialChanges, Flow.ExpectingFinancialChangesNote";
        const validOptions = customFlowFieldList
          ? `${flowStandardList}, ${customFlowFieldList}`
          : flowStandardList;
        return {
          valid: false,
          error: `Invalid flow field: ${fieldRef}. Valid options: ${validOptions}`,
        };
      }

      // QL-13 — Quiz.* fields. The editor shows `{{Quiz.<name>}}` and stores
      // `{{Quiz.<id>}}`, and this runs on the stored form, so accept both. The
      // `.Override` and `.External` variants name the same quiz, so strip the
      // suffix before matching.
      if (fieldRef.toLowerCase().startsWith("quiz.")) {
        const subRef = splitQuizRef(fieldRef.substring("quiz.".length)).ref.toLowerCase();
        if (validQuizIds.has(subRef) || validQuizNames.has(subRef)) {
          continue;
        }
        return {
          valid: false,
          error: `Invalid quiz field: ${fieldRef}. Valid options: ${
            quizFieldList || "no quiz components on this flow yet"
          }`,
        };
      }

      // QL-414 — AdvisorNote.* fields: one per flow page that takes advisor
      // notes. Stored by page id, shown by page name; this runs on the stored
      // form, so a name here is one that matched no page.
      if (hasAdvisorNotePrefix(fieldRef)) {
        const subRef = fieldRef.substring(ADVISOR_NOTE_FIELD_PREFIX.length);
        // The list is not known yet (loading, or its fetch failed): nothing
        // can be said about a stored id, so it is not held against the save. A
        // name is different: it matched no page and would be saved unresolved.
        if (advisorNotePagesUnknown) {
          if (isFieldId(subRef)) continue;
          return {
            valid: false,
            error: `Cannot check ${fieldRef}: the flow's advisor note pages are not loaded. Try again in a moment.`,
          };
        }
        const pageNames = advisorNotePageDisplayNames(advisorNotePages, userLanguage);
        const page = advisorNotePages.find(
          (candidate) =>
            candidate.id.toLowerCase() === subRef.toLowerCase() ||
            pageNames.get(candidate.id)?.toLowerCase() === subRef.toLowerCase(),
        );
        if (page) {
          // A goal page's note belongs to one goal: outside a goal's sections
          // the field has no goal to read it from and would always print empty.
          if (page.pageType === "goal" && sectionScope === "global") {
            return {
              valid: false,
              error: `${ADVISOR_NOTE_FIELD_PREFIX}${pageNames.get(page.id)} is a goal page's note and only prints in a per-goal or per-portfolio section.`,
            };
          }
          continue;
        }
        // A stored id that is no longer listed: the page was deleted or its
        // notes switched off. The PDF prints nothing for it; it must not make
        // the component unsaveable for every later, unrelated edit.
        if (isFieldId(subRef)) continue;
        const options = advisorNotePages
          .filter((candidate) => candidate.pageType === "global" || sectionScope !== "global")
          .map((candidate) => `${ADVISOR_NOTE_FIELD_PREFIX}${pageNames.get(candidate.id)}`)
          .join(", ");
        return {
          valid: false,
          error: `Invalid advisor note field: ${fieldRef}. Valid options: ${
            options || "no page of this flow has advisor notes enabled"
          }`,
        };
      }

      if (!availableFieldNames.includes(fieldRef)) {
        return {
          valid: false,
          error: `Field "${fieldRef}" not found. Available fields: ${availableFieldNames.join(", ") || "none"}`,
        };
      }
    }

    return { valid: true };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate
    const validationError = validateConfig();
    if (validationError) {
      setError(validationError);
      return;
    }

    // Validate field placeholders for text-based components
    if (componentType === "text" && (config as any).content) {
      const validation = validateFieldPlaceholders((config as any).content);
      if (!validation.valid) {
        setError(validation.error || "Invalid field placeholders");
        return;
      }
    }
    // QL-384 — any component's title and description may carry merge fields.
    for (const key of ["title", "description"] as const) {
      const value = config[key];
      if (typeof value !== "string" || !value) continue;
      const validation = validateFieldPlaceholders(
        value,
        componentType === "heading" || componentType === "text",
      );
      if (!validation.valid) {
        setError(validation.error || "Invalid field placeholders");
        return;
      }
    }

    setLoading(true);

    try {
      // Build conditional display config
      const conditionalDisplay = conditionalEnabled
        ? {
            enabled: true,
            sourceFieldId,
            operator,
            value: conditionValue,
          }
        : {
            enabled: false,
            sourceFieldId: "",
            operator: "equals" as ConditionalOperator,
          };

      // Build final config
      const finalConfig = {
        ...config,
        conditionalDisplay,
      };

      const configJson = JSON.stringify(finalConfig);

      if (isEditMode && component) {
        // Update existing component
        await updateComponentMutation.mutateAsync({
          componentId: component.id,
          data: { config: configJson },
        });
        onClose(true);
      } else if (pageId) {
        // Create new component
        await addComponentMutation.mutateAsync({
          pageId,
          data: {
            componentType,
            config: configJson,
            layoutId,
            columnPosition,
          },
        });
        onClose(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred while saving the component");
    } finally {
      setLoading(false);
    }
  };

  // Get field options for conditional display
  type FieldOption = string | { label: string; value: string };

  const getFieldOptions = (fieldId: string): FieldOption[] => {
    if (fieldId === "__flow_status__") {
      // A PDF can only be generated while the instance is `submitted`,
      // `pdf_generated` or `cancelled` (see flow-process-service
      // `assertCanGeneratePdf`). draft / in_progress and the post-PDF review
      // steps can never produce a document, so they aren't offered. We collapse
      // `pdf_generated` (a regeneration of a submitted instance) into
      // `submitted`, leaving the two states an advisor reasons about: a normal
      // submitted session vs a cancelled one.
      return [
        { label: "Submitted", value: "submitted" },
        { label: "Cancelled", value: "cancelled" },
      ];
    }
    if (fieldId === "__classification_type__") {
      return ["Retail", "Professional", "Eligible Counterparty"];
    }
    if (fieldId === "__client_country__") {
      // QL-323 — values are the ISO codes the client record holds (a name
      // could never equal `client.country`); labels localized and sorted with
      // the Nordic markets first, the same list the client form uses.
      return [...countryOptions.priority, ...countryOptions.rest].map((o) => ({
        value: o.code,
        label: o.label,
      }));
    }
    if (fieldId === "__client_type__") {
      return [
        { label: "Person", value: "Person" },
        { label: "Company", value: "Company" },
        { label: "Group", value: "Group" },
      ];
    }
    if (fieldId === PORTFOLIO_TYPE_SOURCE) {
      return [
        { label: "Model Portfolio", value: "model" },
        { label: "Custom Portfolio", value: "custom" },
        { label: "Dynamic Portfolio", value: "dynamic" },
      ];
    }
    if (fieldId === PORTFOLIO_SCHEDULE_SOURCE) {
      return [
        { label: "One-time", value: "one_time" },
        { label: "Recurring", value: "recurring" },
      ];
    }
    // QL-375 group 10b — the portfolio's own mandate verdict
    if (fieldId === PORTFOLIO_HAS_MANDATE_CHECK_SOURCE) {
      return [
        { label: "Yes", value: "true" },
        { label: "No", value: "false" },
      ];
    }
    // QL-72 — model portfolio picked from another risk score
    if (fieldId === PORTFOLIO_MODEL_DEVIATION_SOURCE) {
      return [
        { label: t.modelDeviationYes, value: "yes" },
        { label: t.modelDeviationNo, value: "no" },
        { label: t.modelDeviationNotModel, value: "not_model" },
      ];
    }
    if (fieldId === PORTFOLIO_MANDATE_STATUS_SOURCE) {
      return [
        { label: t.mandateStatusOk, value: "ok" },
        { label: t.mandateStatusAccepted, value: "accepted" },
        { label: t.mandateStatusDeviation, value: "deviation" },
        { label: t.mandateStatusNotAccepted, value: "not_accepted" },
        { label: t.mandateStatusUnchecked, value: "unchecked" },
      ];
    }
    if (fieldId === "__goal_has_risk__") {
      return [
        { label: "Yes", value: "true" },
        { label: "No", value: "false" },
      ];
    }
    // QL-375 group 10 — goal has a mandate in force (the PDF builder emits both states)
    if (fieldId === GOAL_HAS_MANDATE_SOURCE) {
      return [
        { label: "Yes", value: "true" },
        { label: "No", value: "false" },
      ];
    }
    if (fieldId === GOAL_RISK_SCORE_CAPPED_SOURCE) {
      // QL-71 — the four states, ordered as the shared const declares them. The
      // exhaustive map means a fifth state added upstream fails to compile here
      // rather than silently shipping an unlabelled option.
      const capStateLabels: Record<RiskScoreCapState, string> = {
        yes: t.capStateYes,
        no: t.capStateNo,
        no_risk_assessment: t.capStateNoRiskAssessment,
        no_client_field_data: t.capStateNoClientFieldData,
      };
      return RISK_SCORE_CAP_STATES.map((s) => ({ value: s, label: capStateLabels[s] }));
    }
    // QL-774 — the PDF builder seeds "true" / "false" for every instance.
    if (fieldId === FOLLOWUP_MODE_SOURCE) {
      return [
        { label: t.capStateYes, value: "true" },
        { label: t.capStateNo, value: "false" },
      ];
    }
    if (fieldId === "__expecting_financial_changes__") {
      return [
        { label: "Unanswered", value: "unanswered" },
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ];
    }
    if (fieldId === "__trapets_pep__") {
      return [
        { label: "Unchecked", value: "unchecked" },
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ];
    }
    if (fieldId === "__trapets_sanctioned__") {
      return [
        { label: "Unchecked", value: "unchecked" },
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ];
    }
    // QL-68 — Sumsub verified status, the same three values the flow picker
    // offers and the PDF field builder seeds (`sumsub_verified`).
    if (fieldId === "__sumsub_verified__") {
      return [
        { label: "Unchecked", value: "unchecked" },
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ];
    }
    // QL-68 — Sumsub document country: same localised list as Client Country,
    // valued by the alpha-3 code Sumsub reports (raw comparison, no table).
    if (fieldId === "__sumsub_document_country__") {
      return [...countryOptions.priority, ...countryOptions.rest].map((o) => ({
        value: o.alpha3,
        label: o.label,
      }));
    }
    // QL-13 — the three quiz statuses; rules compare the raw status string.
    if (isQuizResultSource(fieldId)) {
      return quizResultValueOptions(templateLanguage);
    }
    // QL-361 — Passed / Not passed / Not answered, or Incomplete for Combined.
    if (isProductAssessmentSource(fieldId)) {
      return assessmentSourceValueOptions(fieldId, templateLanguage);
    }
    // Boolean fields (flow or client custom) offer Yes/No values
    const booleanOptions: FieldOption[] = [
      { label: "Yes", value: "true" },
      { label: "No", value: "false" },
    ];
    // Check for client custom fields (option-based: dropdown/radio/multiselect, or boolean)
    if (fieldId.startsWith("__custom_field_")) {
      const customFieldId = fieldId.replace("__custom_field_", "");
      const customField = clientCustomFields.find((f) => f.id === customFieldId);
      if (customField?.type === "boolean") {
        return booleanOptions;
      }
      if (customField && isOptionFieldType(customField.type) && customField.options) {
        // Rule values store the option id (QL-123); show the localized label.
        // Archived options stay listed only while the rule still uses them.
        return customField.options
          .filter((o) => !o.isArchived || o.id === conditionValue)
          .map((o) => ({
            value: o.id,
            label: getLocalizedValue(o.labelI18n, templateLanguage, tenantDefaultLanguage) || o.id,
          }));
      }
      return [];
    }
    // Check flow fields (option-based: dropdown/radio/multiselect, or boolean)
    const field = flowFields.find((f) => f.id === fieldId);
    if (field?.type === "boolean") {
      return booleanOptions;
    }
    if (!field || !isOptionFieldType(field.type)) {
      return [];
    }
    // Rule values store the option id (QL-201); show the label. Archived
    // options stay listed only while the rule still uses them.
    return (field.options ?? [])
      .filter((o) => !o.isArchived || o.id === conditionValue)
      .map((o) => ({ value: o.id, label: o.label }));
  };

  // Helper to get option value (handles both string and object options)
  const getOptionValue = (option: FieldOption): string =>
    typeof option === "string" ? option : option.value;

  // Helper to get option label (handles both string and object options)
  const getOptionLabel = (option: FieldOption): string =>
    typeof option === "string" ? option : option.label;

  // Resolve the source field's type (client custom field or flow field)
  const sourceCustomField = sourceFieldId.startsWith("__custom_field_")
    ? clientCustomFields.find((f) => f.id === sourceFieldId.replace("__custom_field_", ""))
    : undefined;
  const sourceField = flowFields.find((f) => f.id === sourceFieldId);
  const sourceFieldType = sourceCustomField?.type ?? sourceField?.type ?? "";

  // Check if this is a client custom option field (dropdown/radio/multiselect)
  const isCustomOptionField = !!sourceCustomField && isOptionFieldType(sourceCustomField.type);

  // Multiselect answers are sets — membership operators instead of equality
  const isMultiselectField = sourceFieldType === "multiselect";

  // Boolean answers are Yes/No — equality only, values from a fixed list
  const isBooleanField = sourceFieldType === "boolean";

  // QL-361 — score sources compare a number: Equals / Greater than / Less than
  // only, no "is empty" pair ("has the client answered" is a status question).
  const isScoreSource = isScoreConditionSource(sourceFieldId);

  // Fields that should only show equals/not_equals (no numeric comparisons)
  const isEnumField =
    sourceFieldId === "__flow_status__" ||
    sourceFieldId === FOLLOWUP_MODE_SOURCE ||
    sourceFieldId === "__classification_type__" ||
    sourceFieldId === "__client_country__" ||
    sourceFieldId === "__client_type__" ||
    sourceFieldId === PORTFOLIO_TYPE_SOURCE ||
    sourceFieldId === PORTFOLIO_SCHEDULE_SOURCE ||
    sourceFieldId === PORTFOLIO_HAS_MANDATE_CHECK_SOURCE ||
    sourceFieldId === PORTFOLIO_MANDATE_STATUS_SOURCE ||
    sourceFieldId === PORTFOLIO_MODEL_DEVIATION_SOURCE ||
    sourceFieldId === "__goal_has_risk__" ||
    sourceFieldId === GOAL_HAS_MANDATE_SOURCE ||
    sourceFieldId === GOAL_RISK_SCORE_CAPPED_SOURCE ||
    sourceFieldId === "__expecting_financial_changes__" ||
    sourceFieldId === "__trapets_pep__" ||
    sourceFieldId === "__trapets_sanctioned__" ||
    sourceFieldId === "__sumsub_verified__" ||
    sourceFieldId === "__sumsub_document_country__" ||
    isQuizResultSource(sourceFieldId) ||
    isProductAssessmentSource(sourceFieldId) ||
    isCustomOptionField ||
    isBooleanField;
  // A score source gets its own menu even when an enum rule above covers it.
  const scalarOperators = scalarConditionOperators(sourceFieldId, isEnumField, sourceFieldType);

  const isGoalTypeSource = sourceFieldId === GOAL_TYPE_SOURCE;
  // QL-237 / QL-254 — a stored rule whose operator the menu no longer lists
  // shows, and saves, the one a switch to its source would pick.
  const operator =
    isGoalTypeSource || isMultiselectField
      ? storedOperator
      : menuConditionOperator(storedOperator, scalarOperators, {
          sourceId: sourceFieldId,
          fieldType: sourceFieldType,
        });

  const showValueInput = isGoalTypeSource
    ? operator === "in" || operator === "not_in"
    : isMultiselectField
      ? operator === "contains" || operator === "not_contains"
      : scalarOperators.includes(operator) && isValueRequiredOperator(operator);
  const isDropdownField =
    isOptionFieldType(sourceField?.type ?? "") || sourceField?.type === "boolean" || isEnumField;
  const dropdownOptions = sourceFieldId ? getFieldOptions(sourceFieldId) : [];

  // QL-384 — merge fields in any component's title/description are stored by
  // id and shown by name, as the Table form already does for its own fields.
  // Text, Heading and Table translate their fields themselves.
  const placeholderFieldMap = useMemo(
    () =>
      buildFieldMap(clientCustomFields, flowFields, userLanguage, activeQuizzes, advisorNotePages),
    [clientCustomFields, flowFields, userLanguage, activeQuizzes, advisorNotePages],
  );
  const translatesOwnFields =
    componentType === "text" || componentType === "heading" || componentType === "table";
  const formConfig = useMemo(
    () =>
      translatesOwnFields
        ? config
        : mapTitleAndDescription(config, (value) => transformToDisplay(value, placeholderFieldMap)),
    [config, translatesOwnFields, placeholderFieldMap],
  );
  // Only a title/description the designer actually edited is translated back;
  // an untouched one keeps its stored ids (a name → id round trip could
  // re-point a field whose name another field shares).
  const handleFormConfigChange = (next: Record<string, unknown>) => {
    if (translatesOwnFields) {
      setConfig(next);
      return;
    }
    const stored = { ...next };
    for (const key of ["title", "description"] as const) {
      stored[key] =
        next[key] === formConfig[key]
          ? config[key]
          : typeof next[key] === "string"
            ? transformToStorage(next[key] as string, placeholderFieldMap)
            : next[key];
    }
    setConfig(stored);
  };

  // Render the component-specific form
  const renderComponentForm = () => {
    if (!componentType) return null;

    const formProps = {
      config: formConfig,
      onChange: handleFormConfigChange,
      flowFields,
      quizzes: activeQuizzes,
      advisorNotePages,
      clientCustomFields,
      error,
      setError,
    };

    switch (componentType) {
      case "text":
        return <TextForm {...formProps} config={formConfig as any} sectionScope={sectionScope} />;
      case "heading":
        return (
          <HeadingForm {...formProps} config={formConfig as any} sectionScope={sectionScope} />
        );
      case "table":
        return <TableForm {...formProps} config={formConfig as any} sectionScope={sectionScope} />;
      case "image":
        return <ImageForm {...formProps} config={formConfig as any} />;
      case "advisor_image":
        return <AdvisorImageForm {...formProps} config={formConfig as any} />;
      case "spacer":
        return <SpacerForm {...formProps} config={formConfig as any} />;
      case "portfolio_display":
        return <PortfolioDisplayForm {...formProps} config={formConfig as any} />;
      case "portfolio_xray":
        return (
          <PortfolioXrayForm
            {...formProps}
            config={formConfig as any}
            hasXRayAccess={hasXRayAccess}
          />
        );
      case "cost_chart":
        return <CostChartForm {...formProps} config={formConfig as any} />;
      case "cost_table":
        return <CostTableForm {...formProps} config={formConfig as any} />;
      case "historical_return":
        return <HistoricalReturnForm {...formProps} config={formConfig as any} />;
      case "asset_allocation":
        return <AssetAllocationForm {...formProps} config={formConfig as any} />;
      case "asset_class_forecast":
        return <AssetClassForecastForm {...formProps} config={formConfig as any} />;
      case "expected_value":
        return <ExpectedValueForm {...formProps} config={formConfig as any} />;
      case "risk_assessment":
        return <RiskAssessmentForm {...formProps} config={formConfig as any} />;
      case "portfolio_forecast":
        return <PortfolioForecastForm {...formProps} config={formConfig as any} />;
      case "goal_list":
        return (
          <GoalListForm
            {...formProps}
            config={formConfig as any}
            componentDescription={componentDescriptions.goal_list || ""}
          />
        );
      case "financial_situation":
        return <FinancialSituationForm {...formProps} config={formConfig as any} />;
      case "product_costs":
        return (
          <ProductCostsForm
            {...formProps}
            config={formConfig as any}
            componentDescription={componentDescriptions.product_costs || ""}
            sectionScope={sectionScope}
          />
        );
      case "volatility_chart":
      case "equity_share_chart":
        return (
          <ComplianceChartForm
            {...formProps}
            config={formConfig as any}
            chart={componentType}
            componentDescription={componentDescriptions[componentType] || ""}
          />
        );
      // Title-only components
      case "table_of_contents":
      case "client_classification":
      case "product_assessment":
      case "quiz_results":
      case "trapets_lookup":
      case "sumsub_verification":
      case "enin_lookup":
      case "group_members":
      case "client_parties":
      case "portfolio_costs":
      case "wealth_plan":
      case "sustainability_preferences":
      case "sustainability_status":
      case "client_preferences":
      case "goal_info":
      case "mandate":
      case "mandate_comparison":
      case "mandate_portfolio":
        return (
          <TitleOnlyForm
            {...formProps}
            config={formConfig as any}
            componentDescription={componentDescriptions[componentType] || ""}
            currencyBlockType={isPdfCurrencyBlockType(componentType) ? componentType : undefined}
          />
        );
      // No form needed
      case "page_break":
        return (
          <div className="p-4 bg-muted rounded-md text-center text-muted-foreground">
            <p>{t.noSettingsRequired}</p>
          </div>
        );
      default:
        return null;
    }
  };

  // Check if this component type has a form
  const hasForm = componentType
    ? (componentTypes.find((ct) => ct.type === componentType)?.hasForm ?? false)
    : false;

  // Render conditional display section
  // QL-323 — the source picker is searchable: a flat option list with the same
  // entries, in the same order, the grouped Select used to render.
  const sourceOptions: SearchableSelectOption[] = [];
  const offer = (value: string, label: string, group?: string) =>
    sourceOptions.push({ value, label, group });
  offer("__flow_status__", t.flowStatus);
  offer(FOLLOWUP_MODE_SOURCE, t.followupMode);
  offer("__client_country__", t.clientCountry);
  offer("__client_type__", t.clientType);
  offer("__classification_type__", t.classificationType);
  offer("__trapets_pep__", t.trapetsPep);
  offer("__trapets_sanctioned__", t.trapetsSanctioned);
  offer("__sumsub_verified__", t.sumsubVerified);
  offer("__sumsub_document_country__", t.sumsubDocumentCountry);
  offer("__expecting_financial_changes__", t.expectingFinancialChanges);
  // Goal-scoped sources — only inside per_goal / per_portfolio sections (QL-161)
  if (offerSentinel(GOAL_RISK_SCORE_SOURCE)) offer(GOAL_RISK_SCORE_SOURCE, t.goalRiskScore);
  if (offerSentinel(GOAL_HAS_RISK_SOURCE)) offer(GOAL_HAS_RISK_SOURCE, t.goalHasRisk);
  if (offerSentinel(GOAL_HAS_MANDATE_SOURCE)) offer(GOAL_HAS_MANDATE_SOURCE, t.goalHasMandate);
  if (offerSentinel(GOAL_RISK_SCORE_CAPPED_SOURCE))
    offer(GOAL_RISK_SCORE_CAPPED_SOURCE, t.goalRiskScoreCapped);
  // Goal-type source — only inside per_goal / per_portfolio sections of a
  // template whose collection has goal types (QL-120)
  if (canOfferGoalType) offer(GOAL_TYPE_SOURCE, t.goalType);
  // Portfolio-scoped sources — only inside per_portfolio sections, where the
  // current portfolio exists (QPBI-321)
  if (offerSentinel(PORTFOLIO_TYPE_SOURCE)) {
    offer(PORTFOLIO_TYPE_SOURCE, t.portfolioType);
    offer(PORTFOLIO_SCHEDULE_SOURCE, t.portfolioScheduleType);
    // QL-375 group 10b
    offer(PORTFOLIO_HAS_MANDATE_CHECK_SOURCE, t.portfolioHasMandateCheck);
    offer(PORTFOLIO_MANDATE_STATUS_SOURCE, t.portfolioMandateStatus);
    // QL-72
    offer(PORTFOLIO_MODEL_DEVIATION_SOURCE, t.portfolioModelDeviation);
  }
  // QL-13 — quiz results; global scope, offered in every section
  for (const quiz of activeQuizzes) {
    offer(`${QUIZ_RESULT_SOURCE_PREFIX}${quiz.id}`, `${t.quizField} ${quiz.name}`);
  }
  // QL-361 — quiz scores, one per quiz; global scope like the results
  for (const entry of buildQuizScorePickerEntries(activeQuizzes, userLanguage)) {
    offer(entry.id, entry.label);
  }
  // Client custom fields — every type but "user" (QL-254)
  for (const field of clientCustomFields) {
    if (field.isActive && isConditionSourceCustomFieldType(field.type)) {
      offer(`__custom_field_${field.id}`, `${t.clientField} ${field.name} (${field.type})`);
    }
  }
  for (const field of conditionFlowFields) {
    offer(field.id, `${t.flowField} ${field.name} (${field.type})`);
  }
  // QL-361 — product-assessment statuses and scores; global scope, offered in every section.
  // Offered last: it is the only grouped run, and SearchableSelect never closes a heading, so
  // any ungrouped entry after it would render under "Product assessment".
  for (const entry of buildAssessmentPickerEntries(configuredAssessmentTypes, {
    uiLanguage: userLanguage,
    nameLanguage: templateLanguage,
    fallbackLanguage: tenantDefaultLanguage,
    valueLanguage: templateLanguage,
  })) {
    offer(entry.id, entry.label, entry.group);
  }

  const renderConditionalDisplay = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <Label htmlFor="conditional-enabled" className="text-base">
            {t.enableConditionalDisplay}
          </Label>
          <p className="text-sm text-muted-foreground">{t.showComponentWhen}</p>
        </div>
        <Switch
          id="conditional-enabled"
          checked={conditionalEnabled}
          onCheckedChange={setConditionalEnabled}
        />
      </div>

      {conditionalEnabled && (
        <div className="pl-4 space-y-4 border-l-2">
          <div className="space-y-2">
            <Label htmlFor="source-field">
              {t.when} <span className="text-destructive">*</span>
            </Label>
            <SearchableSelect
              id="source-field"
              value={sourceFieldId}
              onValueChange={handleSourceFieldChange}
              options={sourceOptions}
              placeholder={t.selectField}
              searchPlaceholder={t.searchFields}
              emptyMessage={t.noFieldsAvailable}
              noResultsMessage={t.noFieldsMatch}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="operator">{t.condition}</Label>
            <Select value={operator} onValueChange={(v) => setOperator(v as ConditionalOperator)}>
              <SelectTrigger id="operator">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {isGoalTypeSource ? (
                  // QL-120 — set operators only; a single tick is the equality case.
                  <>
                    <SelectItem value="in">{t.isOneOf}</SelectItem>
                    <SelectItem value="not_in">{t.isNotOneOf}</SelectItem>
                  </>
                ) : isMultiselectField ? (
                  <>
                    <SelectItem value="contains">{t.contains}</SelectItem>
                    <SelectItem value="not_contains">{t.notContains}</SelectItem>
                    <SelectItem value="is_empty">{t.isEmpty}</SelectItem>
                    <SelectItem value="is_not_empty">{t.isNotEmpty}</SelectItem>
                  </>
                ) : (
                  <ScalarOperatorItems operators={scalarOperators} />
                )}
              </SelectContent>
            </Select>
          </div>

          {showValueInput && (
            <div className="space-y-2">
              <Label htmlFor="condition-value">
                {t.value} <span className="text-destructive">*</span>
              </Label>
              {isGoalTypeSource && portfolioCollectionId ? (
                <GoalTypeConditionPicker
                  portfolioCollectionId={portfolioCollectionId}
                  value={conditionValue}
                  onChange={setConditionValue}
                  language={templateLanguage}
                />
              ) : isScoreSource ? (
                <ScoreConditionValueInput
                  id="condition-value"
                  value={conditionValue}
                  onChange={setConditionValue}
                  passScore={scoreSourcePassScore(
                    sourceFieldId,
                    configuredAssessmentTypes,
                    activeQuizzes,
                  )}
                  language={userLanguage}
                />
              ) : sourceFieldId === "__client_country__" ||
                sourceFieldId === "__sumsub_document_country__" ? (
                <SearchableSelect
                  id="condition-value"
                  value={conditionValue}
                  onValueChange={setConditionValue}
                  options={dropdownOptions.map((option) => ({
                    value: getOptionValue(option),
                    label: getOptionLabel(option),
                  }))}
                  placeholder={t.selectValue}
                  searchPlaceholder={t.searchCountries}
                  noResultsMessage={t.noCountriesMatch}
                />
              ) : isDropdownField && dropdownOptions.length > 0 ? (
                <Select value={conditionValue} onValueChange={setConditionValue}>
                  <SelectTrigger id="condition-value">
                    <SelectValue placeholder={t.selectValue} />
                  </SelectTrigger>
                  <SelectContent>
                    {dropdownOptions.map((option) => (
                      <SelectItem key={getOptionValue(option)} value={getOptionValue(option)}>
                        {getOptionLabel(option)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : sourceFieldType === "date" ? (
                // QL-209 — same picker as the date answer, so the value is yyyy-MM-dd
                <DatePicker
                  id="condition-value"
                  value={conditionValue}
                  onChange={setConditionValue}
                />
              ) : sourceFieldType === "datetime" ? (
                // QL-211 — same picker as the datetime answer, so the value is UTC
                <DateTimePicker
                  id="condition-value"
                  value={conditionValue}
                  onChange={setConditionValue}
                />
              ) : (
                <Input
                  id="condition-value"
                  value={conditionValue}
                  onChange={(e) => setConditionValue(e.target.value)}
                  placeholder={t.enterValue}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );

  // Get currently selected component info
  const selectedComponentInfo = availableComponentTypes.find((ct) => ct.type === componentType);

  // Whether the selected component is blocked by collection requirement settings,
  // and the message explaining which setting to enable.
  const selectedBlocked = selectedComponentInfo
    ? isBlockedByCollectionSettings(selectedComponentInfo.type) ||
      isBlockedByEmptyRiskSections(selectedComponentInfo.type)
    : false;
  const selectedBlockedReason =
    selectedComponentInfo && selectedBlocked
      ? isBlockedByEmptyRiskSections(selectedComponentInfo.type)
        ? t.riskPrintsNothing
        : getSettingForComponentType(selectedComponentInfo.type) === "requiresTimeseries"
          ? t.requiresTimeseriesSetting
          : t.requiresAssetAllocationSetting
      : null;

  // QL-212 (D11) — for a placement that DOES print, list what it prints, so the
  // designer sees the consequence of the model's scopes before placing it —
  // the same per-section list the flow picker shows.
  const riskPrintsHere =
    selectedComponentInfo?.type === "risk_assessment" && riskSections?.size
      ? `${t.riskPrintsHere} ${RISK_SECTIONS.filter((s) => riskSections.has(s))
          .map((s) => t[RISK_SECTION_KEYS[s]])
          .join(", ")}`
      : null;
  // A section that owns only the questions prints them only when the template
  // opts into the derivation rows — otherwise the renderer emits nothing.
  const riskNeedsAdjustments =
    selectedComponentInfo?.type === "risk_assessment" &&
    riskSections?.size === 1 &&
    riskSections.has("adjustmentQuestions");

  // Handle next button in selection step
  const handleNext = () => {
    if (componentType) {
      setStep("configure");
    }
  };

  // Handle back button in configure step
  const handleBack = () => {
    setStep("select");
  };

  // Filter components by search query
  const filteredComponentTypes = useMemo(() => {
    if (!searchQuery.trim()) return availableComponentTypes;
    const query = searchQuery.toLowerCase();
    return availableComponentTypes.filter(
      (ct) =>
        ct.label.toLowerCase().includes(query) ||
        ct.description.toLowerCase().includes(query) ||
        ct.detailedDescription.toLowerCase().includes(query),
    );
  }, [availableComponentTypes, searchQuery]);

  // Group available components by category
  const componentsByCategory = useMemo(() => {
    const grouped: Record<string, typeof filteredComponentTypes> = {};
    for (const ct of filteredComponentTypes) {
      if (!grouped[ct.category]) {
        grouped[ct.category] = [];
      }
      grouped[ct.category].push(ct);
    }
    return grouped;
  }, [filteredComponentTypes]);

  // Get categories that have available components
  const availableCategories = componentCategories.filter(
    (cat) => componentsByCategory[cat.id]?.length > 0,
  );

  // Toggle category expansion (accordion style - only one open at a time)
  const toggleCategory = (categoryId: string) => {
    setExpandedCategories((prev) => {
      if (prev.has(categoryId)) {
        // Clicking open category closes it
        return new Set();
      }
      // Opening a category closes all others and selects first component
      const firstInCategory = componentsByCategory[categoryId]?.[0];
      if (firstInCategory) {
        handleComponentTypeChange(firstInCategory.type);
      }
      return new Set([categoryId]);
    });
  };

  // Check if we're in search mode
  const isSearching = searchQuery.trim().length > 0;

  // Render the component type picker (step 1)
  const renderComponentPicker = () => (
    <div className="grid grid-cols-[220px_1fr] gap-4 min-h-[400px]">
      {/* Left: Search and component list */}
      <div className="border rounded-lg overflow-hidden flex flex-col">
        {/* Search input */}
        <div className="p-2 border-b">
          <SearchInput
            data-dialog-guard-ignore
            placeholder={t.searchComponents}
            value={searchQuery}
            onValueChange={setSearchQuery}
            inputClassName="h-8 text-sm"
          />
        </div>
        {/* Component list grouped by collapsible categories */}
        <div className="flex-1 overflow-y-auto max-h-[350px]">
          {availableCategories.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground text-center">
              {t.noComponentsFound}
            </div>
          ) : (
            availableCategories.map((category) => {
              // When searching, show all categories expanded
              const isExpanded = isSearching || expandedCategories.has(category.id);
              const count = componentsByCategory[category.id]?.length || 0;
              const hasSelectedComponent = componentsByCategory[category.id]?.some(
                (ct) => ct.type === componentType,
              );

              return (
                <div key={category.id}>
                  {/* Category header - clickable to expand/collapse (disabled when searching) */}
                  <button
                    type="button"
                    onClick={() => !isSearching && toggleCategory(category.id)}
                    className={`w-full flex items-center justify-between px-3 py-2 bg-muted/50 border-b text-sm font-medium transition-colors ${
                      isSearching ? "cursor-default" : "hover:bg-muted"
                    } ${hasSelectedComponent && !isExpanded ? "text-primary" : ""}`}
                  >
                    <span className="flex items-center gap-2">
                      {!isSearching &&
                        (isExpanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        ))}
                      {category.label}
                    </span>
                    <span className="text-xs text-muted-foreground">({count})</span>
                  </button>
                  {/* Components in category - only show when expanded */}
                  {isExpanded &&
                    componentsByCategory[category.id]?.map((ct) => {
                      const Icon = getComponentIcon(ct.type);
                      const blocked =
                        isBlockedByCollectionSettings(ct.type) ||
                        isBlockedByEmptyRiskSections(ct.type);
                      return (
                        <button
                          key={ct.type}
                          type="button"
                          onClick={() => handleComponentTypeChange(ct.type)}
                          className={`w-full text-left pl-3 pr-3 py-2 border-b last:border-b-0 transition-colors flex items-center gap-2 ${
                            componentType === ct.type
                              ? "bg-primary text-primary-foreground"
                              : "hover:bg-muted"
                          } ${
                            blocked && componentType !== ct.type
                              ? "text-muted-foreground opacity-60"
                              : ""
                          }`}
                        >
                          <Icon className="h-4 w-4 shrink-0" />
                          <div className="font-medium text-sm">{ct.label}</div>
                        </button>
                      );
                    })}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Right: Component description */}
      <div className="p-4 border rounded-lg bg-muted/30">
        {selectedComponentInfo ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              {(() => {
                const SelectedIcon = getComponentIcon(selectedComponentInfo.type);
                return <SelectedIcon className="h-5 w-5 text-muted-foreground" />;
              })()}
              <h3 className="text-lg font-semibold">{selectedComponentInfo.label}</h3>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {selectedComponentInfo.detailedDescription}
            </p>
            {(selectedComponentInfo.requiresPerGoal ||
              selectedComponentInfo.requiresHeadings ||
              selectedComponentInfo.requiresFinancialSituation ||
              selectedComponentInfo.requiresXRay ||
              selectedComponentInfo.requiresRiskModel ||
              selectedComponentInfo.requiresPortfolioCollection ||
              riskPrintsHere ||
              selectedBlockedReason) && (
              <div className="pt-2 space-y-1">
                <p className="text-xs font-medium text-muted-foreground">{t.requirements}</p>
                <ul className="text-xs text-muted-foreground list-disc list-inside space-y-0.5">
                  {selectedComponentInfo.requiresPerGoal && <li>{t.requiresPerGoal}</li>}
                  {selectedComponentInfo.requiresHeadings && <li>{t.requiresHeadings}</li>}
                  {selectedComponentInfo.requiresFinancialSituation && (
                    <li>{t.requiresFinancialSituation}</li>
                  )}
                  {selectedComponentInfo.requiresRiskModel && <li>{t.requiresRiskModel}</li>}
                  {selectedComponentInfo.requiresPortfolioCollection && (
                    <li>{t.requiresPortfolioCollection}</li>
                  )}
                  {selectedComponentInfo.requiresXRay && <li>{t.requiresXRay}</li>}
                  {riskPrintsHere && <li>{riskPrintsHere}</li>}
                  {riskNeedsAdjustments && <li>{t.riskNeedsAdjustments}</li>}
                  {selectedBlockedReason && (
                    <li className="text-destructive">{selectedBlockedReason}</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t.selectComponentDetails}</p>
        )}
      </div>
    </div>
  );

  // Render the configuration form (step 2)
  const renderConfigurationStep = () => (
    <div className="space-y-4">
      {error && (
        <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">{error}</div>
      )}

      {componentType &&
        (hasForm ? (
          <Tabs defaultValue="component" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="component">{t.componentSettings}</TabsTrigger>
              <TabsTrigger value="conditional">{t.conditionalDisplay}</TabsTrigger>
            </TabsList>

            <TabsContent value="component" className="space-y-4 mt-4">
              {renderComponentForm()}
            </TabsContent>

            <TabsContent value="conditional" className="space-y-4 mt-4">
              {renderConditionalDisplay()}
            </TabsContent>
          </Tabs>
        ) : (
          <div className="space-y-4">
            {renderComponentForm()}
            {renderConditionalDisplay()}
          </div>
        ))}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      <DialogScrollContent className="max-w-3xl">
        <DialogScrollHeader>
          <DialogTitle>
            {isEditMode
              ? `${getComponentLabel(componentType as PdfComponentType)} ${t.settings}`
              : step === "select"
                ? t.addPdfComponent
                : `${t.configureComponent} ${getComponentLabel(componentType as PdfComponentType)}`}
          </DialogTitle>
          <DialogDescription>
            {isEditMode
              ? t.configureSettings
              : step === "select"
                ? t.selectComponentType
                : t.configureSettings}
          </DialogDescription>
        </DialogScrollHeader>

        {/* Add mode with steps */}
        {!isEditMode && step === "select" ? (
          <>
            <DialogBody>{renderComponentPicker()}</DialogBody>
            <DialogScrollFooter className="flex justify-between sm:justify-between">
              <DialogCancel>{t.cancel}</DialogCancel>
              <Button
                type="button"
                onClick={handleNext}
                disabled={!componentType || selectedBlocked}
              >
                {t.next}
              </Button>
            </DialogScrollFooter>
          </>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-1 flex-col min-h-0">
            <DialogBody>{renderConfigurationStep()}</DialogBody>
            <DialogScrollFooter className="flex justify-between sm:justify-between">
              <div>
                {!isEditMode && (
                  <Button type="button" variant="outline" onClick={handleBack} disabled={loading}>
                    {t.back}
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <DialogCancel disabled={loading}>{t.cancel}</DialogCancel>
                <Button type="submit" loading={loading} disabled={!componentType}>
                  {isEditMode ? t.save : t.addComponent}
                </Button>
              </div>
            </DialogScrollFooter>
          </form>
        )}
      </DialogScrollContent>
    </Dialog>
  );
}
